import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSavedRituals } from '../context/SavedRitualsContext';
import { getRecipe } from '../data/recipes';
import { Bookmark, Bell, BellOff, Clock, Trash2, ArrowRight, BookOpen, Share2, NotebookPen, Flame, CheckCircle2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const SavedRituals = () => {
  const {
    savedIds,
    removeSaved,
    clearSaved,
    reminders,
    setReminder,
    removeReminder,
    notificationPermission,
    requestNotificationPermission,
    getNote,
    setNote,
    isDoneToday,
    markDone,
    unmarkDone,
    getStreak,
  } = useSavedRituals();

  const shareRitual = async (recipe) => {
    const url = `${window.location.origin}/recipes/${recipe.id}`;
    const shareData = {
      title: `${recipe.title} \u2014 Herbs We Love`,
      text: `${recipe.title}: ${recipe.subtitle}`,
      url,
    };
    if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
      try { await navigator.share(shareData); return; } catch (e) { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied. Share it with someone you love.');
    } catch (e) {
      toast.error('Could not copy link.');
    }
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const saved = savedIds.map(getRecipe).filter(Boolean);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-3">
          <Bookmark className="w-4 h-4" /> Your rituals
        </div>
        <h1 className="font-serif-elegant text-5xl md:text-6xl">Saved Rituals</h1>
        <p className="mt-4 text-[#4a4640] max-w-xl mx-auto">Your personal library of herbal rituals. Add gentle reminders to actually do them.</p>
      </div>

      {/* Notifications banner */}
      {saved.length > 0 && notificationPermission !== 'granted' && (
        <div className="bg-[#efe9db] p-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-sm text-[#4a4640] flex items-start gap-3">
            <Bell className="w-5 h-5 text-[#3a4a3b] shrink-0 mt-0.5" />
            <span>Turn on browser notifications so we can gently nudge you at ritual time (only when this tab is open).</span>
          </div>
          <button onClick={requestNotificationPermission} className="btn-outline whitespace-nowrap">Enable</button>
        </div>
      )}

      {saved.length === 0 ? (
        <div className="py-24 text-center">
          <Bookmark className="w-14 h-14 mx-auto text-[#7a8b6b] mb-6" />
          <p className="font-serif-elegant text-2xl mb-2">No saved rituals yet</p>
          <p className="text-[#6f6a5f] mb-8">Tap the bookmark on any ritual in the journal to add it here.</p>
          <Link to="/recipes" className="btn-primary inline-flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> Browse Recipe Journal
          </Link>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/rituals/calendar" className="inline-flex items-center gap-2 text-sm text-[#3a4a3b] link-underline">
                <Calendar className="w-4 h-4" /> Open ritual calendar
              </Link>
              <Link to="/rituals/digest" className="inline-flex items-center gap-2 text-sm text-[#3a4a3b] link-underline">
                <Calendar className="w-4 h-4" /> This week&rsquo;s digest
              </Link>
            </div>
            <button onClick={clearSaved} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#6f6a5f] hover:text-[#7a3b2e]">
              <Trash2 className="w-4 h-4" /> Clear all
            </button>
          </div>
          <ul className="space-y-6">
            {saved.map(recipe => {
              const reminder = reminders[recipe.id];
              const note = getNote(recipe.id);
              const doneToday = isDoneToday(recipe.id);
              const streak = getStreak(recipe.id);
              const onToggleDone = () => {
                if (doneToday) {
                  unmarkDone(recipe.id);
                  toast.success('Marked as not done today');
                } else {
                  markDone(recipe.id, recipe.title);
                  const s = getStreak(recipe.id) + 1;
                  toast.success(s > 1 ? `Beautiful. ${s}-day streak \u{1F525}` : 'Beautiful. Streak begins today.');
                }
              };
              return (
                <li key={recipe.id} className="bg-[#faf7f1] border border-[#e5decf] p-4 md:p-6 grid grid-cols-1 md:grid-cols-[160px_1fr] gap-5">
                  <Link to={`/recipes/${recipe.id}`} className="block">
                    <div className="aspect-square bg-[#efe9db] overflow-hidden">
                      <img src={recipe.image} alt={recipe.title} className="w-full h-full object-cover" />
                    </div>
                  </Link>
                  <div className="flex flex-col">
                    <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-widest text-[#7a8b6b] mb-2">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {recipe.time}</span>
                      <span>&middot;</span>
                      <span>{recipe.moment}</span>
                    </div>
                    <Link to={`/recipes/${recipe.id}`} className="font-serif-elegant text-2xl mb-1 link-underline">{recipe.title}</Link>
                    <p className="text-sm text-[#4a4640] mb-4 line-clamp-2">{recipe.subtitle}</p>

                    {/* Note preview / editor */}
                    <details className="mb-3 group" open={!!note}>
                      <summary className="cursor-pointer list-none flex items-center gap-2 text-xs uppercase tracking-widest text-[#3a4a3b]">
                        <NotebookPen className="w-3.5 h-3.5" /> {note ? 'My note' : 'Add a note'}
                      </summary>
                      <textarea
                        defaultValue={note}
                        onBlur={(e) => {
                          const val = e.target.value;
                          if (val !== note) {
                            setNote(recipe.id, val);
                            toast.success(val.trim() ? 'Note saved' : 'Note cleared');
                          }
                        }}
                        rows={3}
                        placeholder="How did this ritual feel? What did you notice?"
                        className="mt-2 w-full bg-transparent border border-[#c9c1ad] px-3 py-2 text-sm text-[#4a4640] leading-relaxed focus:outline-none focus:border-[#3a4a3b] resize-none"
                      />
                      <div className="text-[10px] text-[#6f6a5f] mt-1">Auto-saves when you click away</div>
                    </details>

                    {/* Reminder controls */}
                    <div className="mt-auto bg-[#efe9db] p-4 flex flex-wrap items-center gap-3 justify-between">
                      <div className="flex items-center gap-3">
                        <Bell className="w-4 h-4 text-[#3a4a3b]" />
                        <div>
                          <div className="text-xs uppercase tracking-widest text-[#6f6a5f] mb-1">Daily reminder</div>
                          <input
                            type="time"
                            value={reminder?.time || ''}
                            onChange={e => setReminder(recipe.id, e.target.value, recipe.title)}
                            className="bg-transparent border border-[#c9c1ad] px-2 py-1 text-sm focus:outline-none focus:border-[#3a4a3b]"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <button onClick={() => shareRitual(recipe)} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#3a4a3b] hover:text-[#2c3a2d]">
                          <Share2 className="w-3.5 h-3.5" /> Share
                        </button>
                        {reminder?.time && (
                          <button onClick={() => removeReminder(recipe.id)} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#6f6a5f] hover:text-[#7a3b2e]">
                            <BellOff className="w-3.5 h-3.5" /> Off
                          </button>
                        )}
                        <button onClick={() => removeSaved(recipe.id)} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#6f6a5f] hover:text-[#7a3b2e]">
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </button>
                        <Link to={`/recipes/${recipe.id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#3a4a3b] hover:gap-2 transition-all">
                          Open <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          <p className="text-xs text-[#6f6a5f] mt-8 text-center max-w-md mx-auto leading-relaxed">
            Reminders only fire while you have this site open in a browser tab.
            For always-on notifications, add this site to your home screen.
          </p>
        </>
      )}
    </div>
  );
};

export default SavedRituals;
